import type { Command } from 'commander';
import { resolveContext } from '../../lib/global-opts';
import { CliError, ExitCode } from '../../lib/errors';
import { promptSecret } from '../../lib/io-helpers';
import { enterpriseGet } from '../../lib/enterprise-client';
import {
	API_KEY_ENV_VAR,
	KEY_PREFIX,
	getCredentialsPath,
	isValidKeyFormat,
	maskKey,
	saveCredentials,
} from '../../lib/credentials-store';
import { printWarn } from '../../lib/output';
import { channelBanner } from '../../lib/channel';

export interface LoginContext {
	baseUrl: string;
	locale: string;
	timeoutMs: number;
}

/** login 核心（與 commander 解耦供測試）：驗格式 → 打 API 驗 key → 存檔。 */
export async function performLogin(ctx: LoginContext, key: string): Promise<void> {
	const banner = channelBanner();
	if (banner) process.stderr.write(banner);

	if (!isValidKeyFormat(key)) {
		// 不 echo 輸入原文 —— 可能是手滑貼進來的其他 secret
		throw new CliError(
			`That does not look like a valid ${KEY_PREFIX} key. Nothing was saved.`,
			ExitCode.InvalidArgument
		);
	}
	// 任何非 200 都由 enterpriseGet 丟出（401/403 → CliError 附情境提示），不落檔。
	// 打 GET /me 兼作 key 驗證與公司名取得（取代舊的 /jobs?pageSize=1 驗證）。
	const { body } = await enterpriseGet({ ...ctx, apiKey: key }, '/me');
	saveCredentials({
		api_key: key,
		company_name: extractCompanyName(body),
		key_last4: key.slice(-4),
		saved_at: new Date().toISOString(),
	});
}

/**
 * 從 GET /me 的 DataResponse（{ data: { company: { enc_id, name } } }）取公司名。
 * shape 非預期時回空字串 —— login 已驗 key（200），不該因回應格式小變動而失敗；
 * company_name 缺失只讓 whoami 退回顯示 (unknown)。
 */
function extractCompanyName(body: unknown): string {
	if (body && typeof body === 'object') {
		const data = (body as { data?: unknown }).data;
		if (data && typeof data === 'object') {
			const company = (data as { company?: unknown }).company;
			if (company && typeof company === 'object') {
				const name = (company as { name?: unknown }).name;
				if (typeof name === 'string') return name;
			}
		}
	}
	return '';
}

export function registerEnterpriseLogin(parent: Command): void {
	parent
		.command('login')
		.description('Validate and save an enterprise API key (prompts securely; pipe stdin in CI)')
		.action(async (_flags: unknown, command: Command) => {
			const ctx = resolveContext(command);
			const key = await promptSecret(`Paste your API key (${KEY_PREFIX}...): `);
			await performLogin({ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs }, key);
			process.stdout.write(`Logged in. Key ${maskKey(key)} saved to ${getCredentialsPath()}\n`);
			if (process.platform === 'win32') {
				printWarn(
					`On Windows file permissions are best-effort. For stricter isolation, prefer the ${API_KEY_ENV_VAR} env var.`,
					ctx.color
				);
			}
		});
}
